import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { DataGrid, GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import {
  FoodItemSearchingParams,
  FoodRecipeKey,
  PaginatedSearchResultsFoodRecipeKey,
  StepType,
} from "client/jspPlatformExperiment";
import jspPlatformApi from "client/portals";
import {
  isApiError,
  isErrorResponseBodyType,
  toJspApiError,
} from "models/utils/apiUtils";
import { useNavigate, useSearchParams } from "react-router-dom";
import Routes from "models/routes";
import recipeListTableToolbar from "components/food/ui/recipeList/recipeListTableToolbar";
import DataGridListBox from "components/common/ui/DataGridListBox";

// augment the props for the toolbar slot
declare module "@mui/x-data-grid" {
  interface ToolbarPropsOverrides {
    // eslint-disable-next-line
    setSearchingParams: (newSearchingParams: FoodItemSearchingParams) => void;
  }
}

interface RecipeListTableProps {}

const RecipeListTable: FC<RecipeListTableProps> = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<FoodRecipeKey[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Parse pagination from URL params
  const paginationModel = useMemo(() => {
    const page = parseInt(searchParams.get("page") || "0", 10);
    const pageSize = parseInt(searchParams.get("pageSize") || "10", 10);
    return { page, pageSize };
  }, [searchParams]);

  /**
   * Handle pagination model change
   */
  const setPaginationModel = useCallback(
    (newModel: { page: number; pageSize: number }): void => {
      const newParams = new URLSearchParams(searchParams);
      const oldPageSize = paginationModel.pageSize;
      const oldPage = paginationModel.page;

      // Check if pageSize has changed
      if (newModel.pageSize !== oldPageSize) {
        // Calculate the first item index on the old page
        const firstItemIndex = oldPage * oldPageSize;
        // Calculate the equivalent page number for the new pageSize
        const equivalentPage = Math.floor(firstItemIndex / newModel.pageSize);
        newParams.set("page", equivalentPage.toString());
      } else {
        // If pageSize hasn't changed, just use the new page number
        newParams.set("page", newModel.page.toString());
      }

      newParams.set("pageSize", newModel.pageSize.toString());
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams, paginationModel],
  );

  // Parse searching params from URL params
  const searchingParams = useMemo<FoodItemSearchingParams>(() => {
    const keywords = searchParams.get("keywords") || undefined;
    const hasStepTypesParam = searchParams.get("hasStepTypes");
    const hasFoodTagIdsParam = searchParams.get("hasFoodTagIds");

    const hasStepTypesAll =
      hasStepTypesParam && hasStepTypesParam.length > 0
        ? (hasStepTypesParam
            .split(",")
            .filter((type) =>
              [StepType.COOKING, StepType.OVEN, StepType.HUMAN].includes(
                type as StepType,
              ),
            ) as Array<StepType.COOKING | StepType.OVEN | StepType.HUMAN>)
        : undefined;

    const hasFoodTagIdsAll =
      hasFoodTagIdsParam && hasFoodTagIdsParam.length > 0
        ? hasFoodTagIdsParam
            .split(",")
            .map((id) => parseInt(id, 10))
            .filter((id) => !Number.isNaN(id))
        : undefined;

    return {
      keywords: keywords || undefined,
      has_step_types_all: hasStepTypesAll || undefined,
      has_food_tag_ids_all: hasFoodTagIdsAll || undefined,
    };
  }, [searchParams]);

  /**
   * Handle searching params change
   */
  const setSearchingParams = useCallback(
    (newParams: FoodItemSearchingParams): void => {
      const urlParams = new URLSearchParams(searchParams);

      if (newParams.keywords) {
        urlParams.set("keywords", newParams.keywords);
      } else {
        urlParams.delete("keywords");
      }

      if (
        newParams.has_step_types_all &&
        newParams.has_step_types_all.length > 0
      ) {
        urlParams.set("hasStepTypes", newParams.has_step_types_all.join(","));
      } else {
        urlParams.delete("hasStepTypes");
      }

      if (
        newParams.has_food_tag_ids_all &&
        newParams.has_food_tag_ids_all.length > 0
      ) {
        urlParams.set(
          "hasFoodTagIds",
          newParams.has_food_tag_ids_all.join(","),
        );
      } else {
        urlParams.delete("hasFoodTagIds");
      }

      // Reset page to 0 when search params change
      urlParams.set("page", "0");

      setSearchParams(urlParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  /**
   * Initialize default URL params if not present
   */
  useEffect(() => {
    const hasPage = searchParams.has("page");
    const hasPageSize = searchParams.has("pageSize");

    if (!hasPage || !hasPageSize) {
      const newParams = new URLSearchParams(searchParams);
      if (!hasPage) {
        newParams.set("page", "0");
      }
      if (!hasPageSize) {
        newParams.set("pageSize", "10");
      }
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  /**
   * Get the first batch of recipe keys.
   * Abort the previous request when a new search is issued (cleanup runs before next effect).
   */
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    setIsLoading(true);
    jspPlatformApi()
      .recipe.postFoodRecipeSearch({
        body: searchingParams,
        query: {
          number_per_page: paginationModel.pageSize,
          page_index: paginationModel.page,
          return_keys_only: true,
        },
        signal,
      })
      .then((res) => {
        if (signal.aborted) {
          return;
        }
        const data = res.data as PaginatedSearchResultsFoodRecipeKey;
        setRows(data?.data ?? []);
        setRowCount(data?.total_counts ?? -1);
      })
      .catch((reason) => {
        if (
          isApiError(reason) &&
          isErrorResponseBodyType(toJspApiError(reason).body)
        ) {
          // console.log(toJspApiError(reason).body.detail);
        }
      })
      .finally(() => {
        // Keep the loading state on abort: a newer request is already in flight
        if (!signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [paginationModel.page, paginationModel.pageSize, searchingParams]);

  /**
   * Go to recipe detail edit page
   * @param foodId The recipe's food ID, not the recipe ID
   * @param recipeId The recipe's ID
   */
  const handleEditClick = (foodId: number, recipeId: number) => () => {
    navigate(`${Routes.FOOD_RECIPE_EDIT}/${foodId}/${recipeId}`, {
      replace: false,
      state: { editingFoodId: foodId },
    });
  };

  /**
   * Go to recipe view page
   * @param foodId The recipe's food ID, not the recipe ID
   */
  const handleViewClick = (foodId: number) => () => {
    navigate(`${Routes.FOOD_RECIPE_VIEW}/${foodId}`, {
      replace: false,
    });
  };

  const columns: GridColDef<FoodRecipeKey>[] = [
    {
      field: "editAction",
      type: "actions",
      width: 50,
      cellClassName: "actions",
      getActions: ({ id, row }) => {
        const editTo = `${Routes.FOOD_RECIPE_EDIT}/${row.food_id}/${row.id}`;
        return [
          <GridActionsCellItem
            key={`${id}-edit`}
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={(e) => {
              // Handle Ctrl/Cmd + click to open in new tab
              if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                window.open(editTo, "_blank");
              } else {
                handleEditClick(row.food_id, row.id)();
              }
            }}
            onMouseDown={(e) => {
              // Handle middle-click (wheel click) to open in new tab
              if (e.button === 1) {
                e.preventDefault();
                window.open(editTo, "_blank");
              }
            }}
            color="inherit"
          />,
        ];
      },
    },
    { field: "food_id", headerName: "ID", width: 90, editable: false },
    { field: "food_name", headerName: "Dish Name", flex: 180, editable: false },
    { field: "name", headerName: "Recipe Name", flex: 180, editable: false },
    { field: "id", headerName: "Recipe ID", width: 90, editable: false },
    {
      field: "viewAction",
      type: "actions",
      width: 50,
      cellClassName: "actions",
      getActions: ({ id, row }) => {
        const viewTo = `${Routes.FOOD_RECIPE_VIEW}/${row.food_id}`;
        return [
          <GridActionsCellItem
            key={`${id}-view`}
            icon={<AccountTreeOutlinedIcon />}
            label="View"
            className="textPrimary"
            onClick={(e) => {
              if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                window.open(viewTo, "_blank");
              } else {
                handleViewClick(row.food_id)();
              }
            }}
            onMouseDown={(e) => {
              if (e.button === 1) {
                e.preventDefault();
                window.open(viewTo, "_blank");
              }
            }}
            color="inherit"
          />,
        ];
      },
    },
  ];

  return (
    <DataGridListBox>
      <DataGrid
        rows={rows}
        columns={columns}
        loading={isLoading}
        editMode="row"
        slots={{
          toolbar: recipeListTableToolbar,
        }}
        slotProps={{
          toolbar: {
            setSearchingParams,
          },
        }}
        pageSizeOptions={[5, 10, 25, 50]}
        rowCount={rowCount}
        paginationModel={paginationModel}
        paginationMode="server"
        onPaginationModelChange={setPaginationModel}
        initialState={{
          columns: {
            columnVisibilityModel: {
              name: false,
              id: false,
            },
          },
        }}
      />
    </DataGridListBox>
  );
};

export default RecipeListTable;
